import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Typography, 
  Spin,
  message
} from 'antd';
import { UserOutlined, PhoneOutlined } from '@ant-design/icons';
import { apiService } from '../../utils/apiService';
import { API_CONFIG } from '../../utils/constants';
import { loadCountriesConfig, getDefaultCountry } from '../../utils/countriesUtils';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Controller Step Component
 * Handles controller information and customer selection
 */
const ControllerStep = ({ form, formData, updateFormData }) => {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  /**
   * Load companies and countries on component mount
   */
  useEffect(() => {
    loadCompanies();
    loadCountries();
  }, []);

  /**
   * Load countries configuration
   */
  const loadCountries = async () => {
    try {
      setLoadingCountries(true);
      const countries = await loadCountriesConfig();
      setCountryCodes(countries);
      
      // Set default country code if not already set
      if (!formData.controllerCountryCode && countries.length > 0) {
        const defaultCountry = getDefaultCountry(countries);
        updateFormData({ controllerCountryCode: defaultCountry.code });
      }
    } catch (error) {
      console.error('Error loading countries:', error);
      message.error('Failed to load countries configuration');
    } finally {
      setLoadingCountries(false);
    }
  };

  /**
   * Fetch companies from API
   */
  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await apiService.get(API_CONFIG.COMPANY.GET_COMPANY_LIST);
      if (response && Array.isArray(response)) {
        setCompanies(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setCompanies(response.data);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      message.error('Failed to load companies');
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  /**
   * Handle company selection
   */
  const handleCompanySelect = (companyId, option) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    updateFormData({
      selectedCompanyId: companyId,
      selectedCompany: selectedCompany
    });
  };

  /**
   * Handle controller contact number change
   */
  const handleContactNumberChange = (value) => {
    updateFormData({
      controllerContactNo: value
    });
  };

  /**
   * Handle controller name change
   */
  const handleControllerNameChange = (e) => {
    updateFormData({
      controllerName: e.target.value
    });
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        <UserOutlined style={{ marginRight: '8px' }} />
        Controller Information
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Enter the controller details and select the customer company
      </Text>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Form.Item
              label="Controller Name"
              name="controllerName"
              rules={[
                { required: true, message: 'Please enter controller name' },
                { min: 2, message: 'Name must be at least 2 characters' }
              ]}
            >
              <Input
                placeholder="Enter controller name"
                prefix={<UserOutlined />}
                value={formData.controllerName}
                onChange={handleControllerNameChange}
              />
            </Form.Item>
          </div>
          
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Form.Item
              label="Controller Contact Number"
              name="controllerContactNo"
              rules={[
                { required: true, message: 'Please enter contact number' },
                { 
                  pattern: /^\+\d{1,4}\s\d{8,15}$/, 
                  message: 'Please enter a valid phone number with country code' 
                }
              ]}
            >
              <Input.Group compact style={{ display: 'flex' }}>
                <Select
                  placeholder="Country Code"
                  value={formData.controllerCountryCode || '+27'}
                  onChange={(value) => {
                    updateFormData({ controllerCountryCode: value });
                    // Update the full phone number in form
                    const phoneNumber = formData.controllerContactNo?.split(' ')[1] || '';
                    const fullNumber = phoneNumber ? `${value} ${phoneNumber}` : value;
                    form.setFieldsValue({ controllerContactNo: fullNumber });
                    updateFormData({ controllerContactNo: fullNumber });
                  }}
                  style={{ flex: '0 0 auto', minWidth: '120px' }}
                  loading={loadingCountries}
                >
                  {countryCodes.map(item => (
                    <Option key={item.code} value={item.code}>
                      {item.flag} {item.code}
                    </Option>
                  ))}
                </Select>
                <Input
                  style={{ flex: '1' }}
                  placeholder="Enter phone number"
                  prefix={<PhoneOutlined />}
                  value={formData.controllerContactNo?.split(' ')[1] || ''}
                  onChange={(e) => {
                    const code = formData.controllerCountryCode || '+27';
                    const fullNumber = e.target.value ? `${code} ${e.target.value}` : code;
                    form.setFieldsValue({ controllerContactNo: fullNumber });
                    handleContactNumberChange(fullNumber);
                  }}
                />
              </Input.Group>
            </Form.Item>
          </div>
        </div>

        <div style={{ width: '100%' }}>
          <Form.Item
            label="Select Customer Company"
            name="selectedCompanyId"
            rules={[
              { required: true, message: 'Please select a customer company' }
            ]}
          >
            <Select
              placeholder="Search and select a company"
              showSearch
              loading={loadingCompanies}
              filterOption={(input, option) => {
                return option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }}
              value={formData.selectedCompanyId}
              onChange={handleCompanySelect}
              notFoundContent={loadingCompanies ? <Spin size="small" /> : 'No companies found'}
            >
              {companies.map(company => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>

      {formData.selectedCompany && (
        <div style={{ 
          background: '#f6ffed', 
          border: '1px solid #b7eb8f', 
          borderRadius: '6px', 
          padding: '12px',
          marginTop: '16px'
        }}>
          <Text strong style={{ color: '#52c41a', display: 'block' }}>
            Selected Company:
          </Text>
          <Text>{formData.selectedCompany.name}</Text>
          {formData.selectedCompany.uniqueCode && (
            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
              Unique Code: {formData.selectedCompany.uniqueCode}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

export default ControllerStep;
